// @ts-check
import React from "react";
import PropTypes from "prop-types";
import { LuTriangleAlert } from "react-icons/lu";

import { TestableText } from "@components/general/TestableComponents";
import KpiCard from "@components/cards/shared/KpiCard";
import { RAG_META, formatCurrency } from "../shared/businessViewConfig";
import StyledTooltip from "@components/general/StyledTooltip";

/**
 * The RAG dot shown beside a status KPI's label. Addressable in its own right: the dot is
 * the only thing on the card that says which status it is. The colour is looked up from the
 * status rather than passed alongside it, so the two cannot disagree.
 *
 * @param {{ status: import('../shared/businessViewConfig').RagStatus }} props
 */
const StatusDot = ({ status }) => (
  <span
    data-testid={`bv-kpi-dot-${status}`}
    className={`h-3 w-3 shrink-0 rounded-full ${RAG_META[status].dotClass}`}
  />
);

StatusDot.propTypes = { status: PropTypes.string.isRequired };

/**
 * KPI summary row for the Business View page.
 *
 * @param {Object} props
 * @param {import('../shared/businessView.types').BusinessViewKpis} props.kpis
 * @param {boolean} [props.isPartial]  True when one of the workflows was only partially calculated, so the KPIs may be incomplete.
 * @returns {JSX.Element}
 */
const BusinessViewKpiSection = ({ kpis, isPartial = true }) => {
  const { totalFlows, atRisk, monitor, healthy, dailyRiskCost } = kpis || {};

  return (
    <div data-testid="bv-kpi-section" className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
      <KpiCard testIdPrefix="bv-kpi" testKey="total-flows" label="TOTAL FLOWS" value={totalFlows ?? 0} />
      <KpiCard
        testIdPrefix="bv-kpi"
        testKey="at-risk"
        label="AT RISK"
        value={atRisk ?? 0}
        labelAdornment={<StatusDot status="at_risk" />}
      />
      <KpiCard
        testIdPrefix="bv-kpi"
        testKey="monitor"
        label="MONITOR"
        value={monitor ?? 0}
        labelAdornment={<StatusDot status="monitor" />}
      />
      <KpiCard
        testIdPrefix="bv-kpi"
        testKey="healthy"
        label="HEALTHY"
        value={healthy ?? 0}
        labelAdornment={<StatusDot status="healthy" />}
      />
      <KpiCard
        testIdPrefix="bv-kpi"
        testKey="daily-risk-cost"
        label="DAILY RISK COST"
        labelAdornment={
          isPartial && (
            <StyledTooltip content="Some of the workflows were only partially calculated. Please review the individual workflow for details." placement="top">
              <TestableText dataTestId="bv-kpi-daily-risk-cost-partial" variant="span">
                <LuTriangleAlert size={24} color="orange" />
              </TestableText>
            </StyledTooltip>
          )
        }
        value={
            <span className="flex items-baseline gap-1">
              {formatCurrency(dailyRiskCost)}
              <span className="text-sm font-light text-gray-700 dark:text-gray-300">/ day</span>
            </span>
        	}
      />
    </div>
  );
};

BusinessViewKpiSection.propTypes = {
  kpis: PropTypes.shape({
    totalFlows: PropTypes.number,
    atRisk: PropTypes.number,
    monitor: PropTypes.number,
    healthy: PropTypes.number,
    dailyRiskCost: PropTypes.number,
  }).isRequired,
  isPartial: PropTypes.bool,
};

export default BusinessViewKpiSection;
